import React, { useState } from 'react';
import { ClockIcon, StarIcon } from '@heroicons/react/24/outline';
import DaySchedule from './DaySchedule';
import CourseDetailModal from './CourseDetailModal';

export default function CourseTable({ courses, isLoading, onAddToCal }) {
  const [selectedCourse, setSelectedCourse] = useState(null);

  if (isLoading) {
    return <TableLoadingSkeleton />;
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-dark-600">
        No courses found.
      </div>
    );
  }

  const handleRowClick = (course) => {
    setSelectedCourse(course);
  };

  const formatInstructors = (instructors) => {
    if (!instructors) return 'N/A';
    
    // Handle JSON string format from API
    let parsedInstructors = instructors;
    if (typeof instructors === 'string') {
      try {
        parsedInstructors = JSON.parse(instructors);
      } catch {
        return instructors; // Return as-is if not JSON
      }
    }
    
    if (!Array.isArray(parsedInstructors)) return 'N/A';
    
    const validInstructors = parsedInstructors
      .filter(Boolean)
      .map((instructor) => 
        instructor
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
      );

    if (validInstructors.length === 0) return 'N/A';
    if (validInstructors.length <= 2) {
      return validInstructors.join(', ');
    }
    return `${validInstructors.slice(0, 2).join(', ')} +${validInstructors.length - 2} more`;
  };

  const getWorkloadBadgeClass = (hours) => {
    if (!hours) return 'bg-gray-100 text-gray-700 dark:bg-dark-300 dark:text-dark-700';
    if (hours < 5) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    if (hours < 10) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (hours < 15) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  const getRatingBadgeClass = (rating) => {
    if (!rating) return 'bg-gray-100 text-gray-700 dark:bg-dark-300 dark:text-dark-700';
    if (rating >= 4.5) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    if (rating >= 4.0) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (rating >= 3.5) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-400">
          <thead className="bg-gray-50 dark:bg-dark-300">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
                Course
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
                Instructors
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
                Term
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
                Schedule
              </th>
              {onAddToCal && (
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
                  Add
                </th>
              )}
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
                Workload & Rating
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-200 divide-y divide-gray-200 dark:divide-dark-400">
            {courses.map((course) => (
              <tr
                key={`${course.course_id}-${course.class_number}`}
                className="hover:bg-gray-50 dark:hover:bg-dark-300 cursor-pointer transition-colors"
                onClick={() => handleRowClick(course)}
              >
                {/* Course Column */}
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-500 dark:text-dark-600">
                      {course.class_tag || 'N/A'}
                    </div>
                    <div className="font-medium text-gray-900 dark:text-dark-800 line-clamp-2">
                      {course.class_name || 'N/A'}
                    </div>
                  </div>
                </td>

                {/* Instructors Column */}
                <td className="px-4 py-4 text-sm text-gray-700 dark:text-dark-700">
                  {formatInstructors(course.instructors)}
                </td>

                {/* Term Column */}
                <td className="px-4 py-4 text-sm text-gray-700 dark:text-dark-700 whitespace-nowrap">
                  {course.term || 'N/A'}
                </td>

                {/* Schedule Column */}
                <td className="px-4 py-4">
                  <div className="flex justify-center">
                    {course.start_times && course.end_times ? (
                      <DaySchedule
                        days={course.days}
                        startTimes={course.start_times}
                        endTimes={course.end_times}
                      />
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-dark-500 italic">
                        No scheduled classes
                      </span>
                    )}
                  </div>
                </td>

                {/* Add to Calendar Button */}
                {onAddToCal && (
                  <td className="px-4 py-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddToCal(course);
                      }}
                      className="px-3 py-1.5 bg-accent-primary hover:bg-accent-secondary text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      + Calendar
                    </button>
                  </td>
                )}

                {/* Workload & Rating Column */}
                <td className="px-4 py-4">
                  <div className="flex justify-end space-x-2">
                    {course.mean_hours && (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getWorkloadBadgeClass(
                          course.mean_hours
                        )}`}
                      >
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {course.mean_hours.toFixed(1)}h/wk
                      </span>
                    )}
                    {course.overall_score_course_mean && (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRatingBadgeClass(
                          course.overall_score_course_mean
                        )}`}
                      >
                        <StarIcon className="h-3 w-3 mr-1" />
                        {course.overall_score_course_mean.toFixed(2)}/5
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Course Detail Modal */}
      {selectedCourse && (
        <CourseDetailModal
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
        />
      )}
    </>
  );
}

function TableLoadingSkeleton() {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-400">
        <thead className="bg-gray-50 dark:bg-dark-300">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
              Course
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
              Instructors
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
              Term
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
              Schedule
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-600 uppercase tracking-wider">
              Workload & Rating
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-dark-200 divide-y divide-gray-200 dark:divide-dark-400">
          {Array.from({ length: 10 }).map((_, index) => (
            <tr key={index}>
              {Array.from({ length: 5 }).map((_, cellIndex) => (
                <td key={cellIndex} className="px-4 py-4">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-dark-400 rounded w-full mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-dark-400 rounded w-3/4"></div>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}